"use client";

import CommonHeader from "@/components/common/CommonHeader";
import { FilterAndSorting } from "@/components/products/FilterAndSorting";
import ProductCartComponent from "@/components/products/ProductCartComponent";
import useCartStore from "@/store/cartStore";
import { useEffect, useState } from "react";
import { ProductList } from "@/components/products/ProductList";
import { WebFilter } from "@/components/products/WebFilter";
import useProductStore from "@/store/productStore";
import { toast } from "@/hooks/use-toast";
import Loader from "@/components/common/loader";
import { useSearchParams } from "next/navigation";
import InfiniteScroll from "@/components/common/InfiniteScroll";

export default function ProductsPage() {
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState<Record<string, string[]>>({});

  const { fetchProducts, productList, productMetadata, loadMoreProducts } =
    useProductStore();
  const searchParams = useSearchParams();
  useEffect(() => {
    filterAndSort();
    loadProducts();
  }, [searchParams]);

  const filterAndSort = () => {
    let fltr: Record<string, string[]> = {};
    let sort: Record<string, string[]> = {};
    searchParams?.forEach((value, key) => {
      if (key.startsWith("f_")) {
        const filterKey = key.slice(2);
        fltr[filterKey] = value.split("-");
      } else if (key.startsWith("s_")) {
        const sortKey = key.slice(2);
        sort[sortKey] = [value];
      }
    });
    setFilter(fltr);
    setSort(sort);
    return { fltr, sort };
  };
  const loadProducts = async () => {
    setLoading(true);
    const { fltr, sort } = filterAndSort();
    const result = await fetchProducts({ filter: fltr, sort: sort });
    setLoading(false);
    if (!result.status) {
      toast({
        variant: "destructive",
        title: "Error",
        description: result.error,
      });
    }
  };
  const { cart } = useCartStore();
  useEffect(() => {
    let ro: ResizeObserver | null = null;

    const rafMeasure = () => requestAnimationFrame(measure);

    function measure() {
      const header = document.querySelector(
        ".page-top-header"
      ) as HTMLElement | null;
      const content = document.querySelector(
        ".product-content"
      ) as HTMLElement | null;
      const bottom = document.querySelector(
        ".page-bottom"
      ) as HTMLElement | null;

      if (!content || !header) return;

      // Check if we're in mobile view (below md breakpoint, typically 768px)
      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        const headerH = header.getBoundingClientRect().height;
        const bottomH = bottom?.getBoundingClientRect().height ?? 0;

        // Update padding to account for fixed header/footer on mobile
        content.style.paddingTop = `${headerH + 16}px`;
        content.style.paddingBottom = `${bottomH + 16}px`;

        // Calculate and set minimum height for proper scrolling
        const viewportH = window.innerHeight;
        const minContentH = Math.max(0, viewportH - headerH - bottomH);
        content.style.minHeight = `${minContentH}px`;
      } else {
        // Reset padding on desktop view
        content.style.paddingTop = "";
        content.style.paddingBottom = "";
        content.style.minHeight = "";
      }
    }

    // Initial measurement
    rafMeasure();
    setTimeout(rafMeasure, 100);
    setTimeout(rafMeasure, 300); // Extra delay for fonts/images

    // Responsive updates
    window.addEventListener("resize", rafMeasure);

    // Observe size changes
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(rafMeasure);
      const header = document.querySelector(".page-top-header");
      const bottom = document.querySelector(".page-bottom");

      header && ro.observe(header);
      bottom && ro.observe(bottom);
    }

    return () => {
      window.removeEventListener("resize", rafMeasure);
      ro?.disconnect();
    };
  }, []);

  return (
    <>
      <div className="page-top-header h-[104px] fixed top-0 left-0 right-0 z-20 bg-white md:hidden">
        <CommonHeader title="store" style="relative border-b" />
        <FilterAndSorting />
      </div>

      <div className="product-content max-w-7xl mx-auto px-4 md:px-0 md:py-10 flex gap-12">
        <div className="hidden md:block w-64 shrink-0">
          <WebFilter />
        </div>

        <div className="flex-1">
          {loading ? (
            <Loader />
          ) : productList?.length > 0 ? (
            <InfiniteScroll
              loading={loading}
              hasMore={productMetadata?.totalPages > productMetadata?.page}
              loadMore={() =>
                loadMoreProducts({
                  filter,
                  page: productMetadata?.page + 1,
                  limit: productMetadata?.limit,
                })
              }
            >
              <ProductList products={productList} />
            </InfiniteScroll>
          ) : (
            <div className="flex flex-col min-h-[50vh] justify-center items-center">
              <p className="text-gray-400  text-base">No products found</p>
              {Object.keys(filter).length > 0 && (
                <p className="text-xs text-gray-300 mt-2">
                  Please modify the filter
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {cart.length > 0 && (
        <div className="page-bottom h-[76px] fixed left-0 right-0 bottom-0 p-4 z-10 bg-white/50 backdrop-blur-xl shadow-[0_-5px_20px_0px_rgba(0,0,0,0.1)] animated-slideUp md:hidden">
          <ProductCartComponent />
        </div>
      )}
    </>
  );
}
